import {
  Avatar,
  Badge,
  ButtonList,
  Collapsible,
  Divider,
  DropdownButton,
  FlexBox,
  Icon,
  IconButton,
  Text,
  Tooltip,
} from '@dixa/lib-shared-habla';
import { typography } from '@dixa/lib-shared-tokens';
import classnames from 'classnames';
import {
  ConversationsChannels,
  MessageSourceLink,
} from 'lib-shared-legacy-types';
import type {
  ConversationMessageDeliveryEventComputed,
  User,
} from 'lib-shared-types-api';
import { memo, useEffect, useRef, useState } from 'react';

import { channelToSanitizationProfile } from 'app/utils/htmlSanitizer';
import {
  ConversationMessageDeliveryStatus,
  ConversationSourceLinks,
} from 'components/conversation';
import { ConversationTimestamp } from 'components/conversation/components/conversationTimestamp/conversationTimestamp';
import { SafeInnerHtmlSetter } from 'components/shared/util/SafeInnerHtmlSetter';
import { getUserDisplayName } from 'utils/user';

import markdownToHTML from '../../../../app/utils/markdownToHTML';

import styles from './message.module.scss';

export type MessageProps = {
  computedDeliveryEvent?: ConversationMessageDeliveryEventComputed;
  csid?: number;
  messageId?: string;
  /** Extra classnames */
  className?: string;
  /** Author info */
  author: User;
  /** The description presented after the author name. */
  authorDescription?: React.ReactNode;
  /** Is the message internal? */
  internal?: boolean;
  /** The message markup (will be dangerously set) */
  markup?: string;
  /** Display both the markup and the content. */
  showMarkupAndContent?: boolean;
  /** Timestamp */
  date?: string;
  /** Is this the newest message? */
  newest?: boolean;
  /** Indicates flag of the first received message. */
  first?: boolean;
  /** Hide avatar, name and timestamp */
  simple?: boolean;
  /** Loading */
  loading?: boolean;
  /** Did the message fail */
  failed?: boolean | string;
  /** The low level failure reason */
  failureReason?: string;
  /** Is the users own message? */
  own?: boolean;
  /** Is an agents message? */
  isAgent?: boolean;
  /** Custom content to show instead of markup */
  content?: React.ReactNode;
  /** Images */
  images?: React.ReactNode;
  /** Attachments */
  attachments?: Array<React.ReactNode>;
  /** Menu */
  menu?: React.ReactNode[];
  /** Name menu */
  nameMenu?: React.ReactNode;
  /**
   * Content shown on its own line below the author name (e.g. email
   * recipients).
   */
  nameDetails?: React.ReactNode;
  /** Channel (used for sanitisation) */
  channel?: ConversationsChannels | 'notes' | null;
  onClick?: (e: React.MouseEvent) => void;
  /** Whether the message is an auto reply. */
  isAutoReply?: boolean;
  /** The name of the template used in the auto reply. */
  renderedTemplateName?: string;
  /** Attributes of the conversation this message comes from. */
  conversationAttributes?: Record<string, string>;
  /** Additional buttons to be added between "Scroll to top" and "Menu" buttons. */
  gutterButtons?: React.ReactNode;
  /** Remove the reserved gutter space on the right, in context */
  removeGutter?: boolean;
  /** Conversation */
  conversation?: any;
  /** Whether the message is anonymized or not */
  isAnonymized?: boolean;
  /** Source links */
  sourceLinks?: MessageSourceLink[];
  /** Reason auto-redaction failed, if applicable */
  autoRedactionFailureReason?: string;
};

type MarkupAndContentProps = Pick<
  MessageProps,
  'content' | 'markup' | 'showMarkupAndContent' | 'channel'
>;

type CollapsibleMarkupAndContentProps = Pick<
  MessageProps,
  | 'renderedTemplateName'
  | 'content'
  | 'markup'
  | 'showMarkupAndContent'
  | 'channel'
>;

const MarkupAndContent = memo(function MarkupAndContent({
  content,
  markup,
  showMarkupAndContent,
  channel,
}: MarkupAndContentProps) {
  const sanitizationProfile = channel
    ? channelToSanitizationProfile[channel]
    : 'default';

  return (
    <div className={styles.messageContentWrap}>
      {markup && (!content || showMarkupAndContent) && (
        <SafeInnerHtmlSetter
          element="div"
          content={markdownToHTML(markup)}
          profile={sanitizationProfile}
          className={styles.text}
          dir="auto"
        />
      )}

      {content}
    </div>
  );
});

const CollapsibleMarkupAndContent = memo(function CollapsibleMarkupAndContent({
  renderedTemplateName,
  content,
  markup,
  showMarkupAndContent,
  channel,
}: CollapsibleMarkupAndContentProps) {
  const [isAutoReplyContentOpen, setIsAutoReplyContentOpen] = useState(false);

  const toggleAutoReplyContent = (): void => {
    setIsAutoReplyContentOpen(
      (isAutoReplyContentOpen) => !isAutoReplyContentOpen,
    );
  };

  return (
    <Collapsible
      isOpen={isAutoReplyContentOpen}
      header={
        <FlexBox alignItems="center" gap="100">
          <Text.Paragraph>{renderedTemplateName}</Text.Paragraph>
          <IconButton
            icon={isAutoReplyContentOpen ? 'angle-up' : 'angle-down'}
            variant="tertiary"
            onClick={toggleAutoReplyContent}
          />
        </FlexBox>
      }
    >
      <Divider className={styles.autoReplyDivider} />
      <MarkupAndContent
        content={content}
        markup={markup}
        showMarkupAndContent={showMarkupAndContent}
        channel={channel}
      ></MarkupAndContent>
    </Collapsible>
  );
});

export const Message = memo(function Message(props: MessageProps): JSX.Element {
  const {
    attachments,
    author,
    authorDescription,
    channel,
    className,
    computedDeliveryEvent,
    content,
    conversation,
    conversationAttributes,
    csid,
    date,
    failed,
    failureReason,
    first,
    gutterButtons,
    images,
    internal,
    isAgent,
    isAnonymized,
    isAutoReply,
    loading,
    markup,
    menu,
    messageId,
    nameMenu,
    nameDetails,
    removeGutter,
    newest,
    onClick,
    own,
    renderedTemplateName,
    showMarkupAndContent,
    simple,
    sourceLinks,
    autoRedactionFailureReason,
    ...otherProps
  } = props;
  const measureRef = useRef<HTMLDivElement>(null);
  const gotoRef = useRef<HTMLDivElement>(null);
  const rootRef = useRef<HTMLDivElement>(null);

  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const [stuck, setStuck] = useState(false);

  useEffect(() => {
    const measureElement = measureRef.current;
    const rootElement = rootRef.current;

    const intersectionObserver = new IntersectionObserver(
      ([measureElementEntry]) =>
        setStuck(measureElementEntry.intersectionRatio < 1),
      {
        root: rootElement?.closest('.messages'),
        threshold: [0, 1],
      },
    );

    if (measureElement) {
      intersectionObserver.observe(measureElement);
    }

    return () => intersectionObserver.disconnect();
  }, []);

  const handleMenuToggling = (isOpen: boolean) => {
    setIsMenuOpen(isOpen);
  };

  const goToTop = () => {
    if (gotoRef.current) {
      gotoRef.current.scrollIntoView({ behavior: 'smooth' });
    }
  };

  const rootClass = classnames(
    {
      [styles.message]: true,
      [styles.messageInternal]: !!internal,
      [styles.messageSimple]: !!simple,
      [styles.messageFailed]: !!failed,
      [styles.messageLoading]: !!loading,
      [styles.messageOwn]: !!own,
      [styles.messageAgent]: isAgent,
      [styles.messageActive]: isMenuOpen,
      [styles.messageStuck]: stuck,
    },
    className,
  );

  const showDotDotDot = menu && menu.length > 0;
  const showGoToTop = showDotDotDot;

  return (
    <div {...otherProps} className={rootClass} ref={rootRef} onClick={onClick}>
      {showGoToTop && (
        <>
          <div className={styles.measure} ref={measureRef} />
          <div className={styles.goto} ref={gotoRef} />
        </>
      )}

      {!simple && (
        <div className={styles.avatarWrap}>
          <Avatar user={author} />
        </div>
      )}

      <div className={styles.messageWrap}>
        {!simple && (
          <FlexBox className={styles.name} alignItems="center" gap="200">
            <span>{getUserDisplayName(author)}</span>
            {authorDescription}
            {isAutoReply && (
              <Badge
                className={styles.autoReply}
                size="small"
                variant="primary"
              >
                Auto response
              </Badge>
            )}
            {internal && <> (Internal Note)</>}
            {!!nameMenu && <div className={styles.nameMenu}>{nameMenu}</div>}
          </FlexBox>
        )}

        {!simple && !!nameDetails && (
          <div className={styles.nameDetails}>{nameDetails}</div>
        )}

        {isAutoReply ? (
          <CollapsibleMarkupAndContent
            renderedTemplateName={renderedTemplateName}
            content={content}
            markup={markup}
            showMarkupAndContent={showMarkupAndContent}
            channel={channel}
          ></CollapsibleMarkupAndContent>
        ) : (
          <MarkupAndContent
            content={content}
            markup={markup}
            showMarkupAndContent={showMarkupAndContent}
            channel={channel}
          ></MarkupAndContent>
        )}

        {images}

        {!!attachments && attachments.length > 0 && (
          <div className={styles.attachments}>
            {attachments.map((attachment, index) => (
              <div key={index} className={styles.attachment}>
                {attachment}
              </div>
            ))}
          </div>
        )}

        {!!sourceLinks && sourceLinks.length > 0 && (
          <ConversationSourceLinks sourceLinks={sourceLinks} />
        )}
      </div>

      <div>
        <div
          className={classnames(styles.gutter, {
            [styles.removeGutter]: removeGutter,
          })}
        >
          <div className={styles.status}>
            <FlexBox gap="200" alignItems="center">
              {!simple && !!date && (
                <ConversationTimestamp
                  time={date}
                  relative={newest}
                  shouldDateFormatPersist={!newest}
                  textProps={{ className: styles.timestamp }}
                />
              )}

              {csid && computedDeliveryEvent && messageId && !isAnonymized && (
                <ConversationMessageDeliveryStatus
                  csid={csid}
                  computedDeliveryEvent={computedDeliveryEvent}
                  messageId={messageId}
                />
              )}

              {autoRedactionFailureReason && (
                <Tooltip
                  wrap="span"
                  content={`Automatic content redaction failed and it will not be retried. Please manually redact the message if necessary.`}
                >
                  <Icon
                    color={typography.color.warning}
                    name="exclamation-circle-filled"
                  />
                </Tooltip>
              )}
            </FlexBox>
          </div>
          <FlexBox
            className={styles.gutterActions}
            gap="200"
            alignItems="center"
          >
            {showGoToTop && (
              <IconButton
                variant="secondary"
                icon="arrow-up"
                onClick={goToTop}
                // Set here so Habla's own transition cannot win on emit order.
                style={{ transition: 'opacity 250ms ease-out' }}
                className={styles.stuckButton}
              />
            )}
            {gutterButtons}
            {showDotDotDot && (
              <DropdownButton
                noArrow
                icon="dots-h"
                dropdown={<ButtonList>{menu}</ButtonList>}
                onToggle={handleMenuToggling}
              />
            )}
          </FlexBox>
        </div>
      </div>
    </div>
  );
});
